import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AnimatePresence, motion } from 'framer-motion';
import { EFonts, EVariables } from 'libs/styles/styling';
import { useEffect, useState } from 'react';
import styled from 'styled-components/macro';

interface Props {
    color: string;
    background: string;
    title: string;
    description: string;
    items?: string[];
    className?: string;
}

export const WelcomeGuide = (props: Props): JSX.Element => {
    const [open, setOpen] = useState(localStorage.getItem('welcome') !== 'closed');
    const [skipAnimation, setSkipAnimation] = useState(false);
    const [decided, setDecided] = useState(false);

    useEffect(() => {
        if (localStorage.getItem('welcome') !== 'closed') {
            setSkipAnimation(true);
        }
        setDecided(true);
    }, []);

    useEffect(() => {
        if (open) {
            setSkipAnimation(false);
            setDecided(true);
        }
    }, [open]);

    const Anim = {
        initial: { height: 0 },
        animate: { height: 'unset' },
        transition: { duration: 0.2, ease: 'easeInOut' },
        exit: { height: 0 }
    };

    const anim = skipAnimation || !decided ? {} : Anim;

    return (
        <S_WelcomeGuide
            className={props.className}
            onClick={() => {
                localStorage.setItem('welcome', open ? 'closed' : 'open');
                setSkipAnimation(false);
                setOpen(!open);
            }}
            $background={props.background}
        >
            <S_Head>
                <S_FontIcon $color={props.color} icon={['far', open ? 'minus' : 'plus']} />
                <S_Title $color={props.color}>{props.title}</S_Title>
            </S_Head>
            <AnimatePresence>
                {open && (
                    <S_Body {...anim}>
                        <S_Description $color={props.color}>{props.description}</S_Description>
                        {props.items && (
                            <S_List>
                                {props.items.map((item) => {
                                    return (
                                        <S_Item key={item}>
                                            <S_Circle $color={props.color} />
                                            <S_Text $color={props.color}>{item}</S_Text>
                                        </S_Item>
                                    );
                                })}
                            </S_List>
                        )}
                    </S_Body>
                )}
            </AnimatePresence>
        </S_WelcomeGuide>
    );
};

const S_WelcomeGuide = styled.div<{ $background: string }>`
    width: 100%;
    border-radius: ${EVariables.Radius2};
    padding: 35px 45px;
    display: flex;
    flex-direction: column;
    background-color: linear-gradient(180deg, rgba(203, 225, 244, 1) 0%, #f99a6d);
    background-image: url(${(props) => props.$background});
    background-size: cover;
    cursor: pointer;
`;

const S_Head = styled.div`
    display: flex;
    align-items: center;
    gap: 20px;
`;

const S_Body = styled(motion.div)`
    display: flex;
    flex-direction: column;
    overflow: hidden;
`;

const S_FontIcon = styled(FontAwesomeIcon)<{ $color: string }>`
    font-size: 24px;
    color: ${(props) => props.$color};
`;

const S_Title = styled.div<{ $color: string }>`
    font-size: 26px;
    letter-spacing: ${EVariables.Spacing2};
    color: ${(props) => props.$color};
    font-family: ${EFonts.Bold};
`;

const S_Description = styled.div<{ $color: string }>`
    font-size: 15px;
    color: ${(props) => props.$color};
    font-family: ${EFonts.InterMedium};
    margin-top: 20px;
    line-height: 26px;
`;

const S_List = styled.div`
    margin-top: 30px;
    display: flex;
    flex-direction: column;
    gap: 20px;
`;

const S_Item = styled.div`
    display: flex;
    align-items: center;
`;

const S_Circle = styled.div<{ $color: string }>`
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: ${(props) => props.$color};
    margin: 0 15px 0 0;
`;

const S_Text = styled.div<{ $color: string }>`
    font-size: 15px;
    color: ${(props) => props.$color};
    font-family: ${EFonts.InterMedium};
`;
